// === Pedagogy Reviewer ===
// Slide-in drawer that shows the LLM's structured pedagogy review of the
// current section workflow. Renders inside the WorkflowTab graph container
// (same scope as ShowCodePanel) so it never stacks a modal over a modal —
// per CLAUDE.md "No popup over a popup".
//
// Each review row carries a severity (info / warn / fix), a category, a
// message and (optionally) a targetItemId. Clicking a row with a target
// dispatches selectInteractiveItem and emits a scroll-to-node event so the
// CanvasWorkflowPanel can focus the matching graph node.
import React from "react";
import { useDispatch } from "react-redux";
import { selectInteractiveItem } from "../../../common/actions";
import "./ReviewDrawer.scss";

const SEVERITY_META = {
  fix: { icon: "error_outline", className: "review-drawer__row--fix" },
  warn: { icon: "warning_amber", className: "review-drawer__row--warn" },
  info: { icon: "info_outline", className: "review-drawer__row--info" },
};

function severityMeta(sev) {
  return SEVERITY_META[sev] || SEVERITY_META.info;
}

export default function ReviewDrawer({
  show,
  loading,
  error,
  reviews,
  onClose,
  onRetry,
  sectionId,
}) {
  const dispatch = useDispatch();
  if (!show) return null;

  const list = Array.isArray(reviews) ? reviews : [];

  const focusItem = (targetItemId) => {
    if (!targetItemId) return;
    // selectInteractiveItem(boxId, itemId): the review references either a
    // pluginBoxId or a trigger/item id. Pass the same value as boxId so
    // CanvasWorkflowPanel's selection sync highlights the matching node;
    // unknown ids no-op cleanly in the reducer.
    dispatch(selectInteractiveItem(targetItemId, null));
    if (typeof document !== "undefined" && typeof CustomEvent === "function") {
      document.dispatchEvent(
        new CustomEvent("aidea-workflow-scroll-to-node", {
          detail: { nodeId: targetItemId },
        })
      );
    }
  };

  return (
    <aside
      className="review-drawer"
      role="complementary"
      aria-label="Pedagogy review"
      data-testid="review-drawer"
    >
      <header className="review-drawer__head">
        <div className="review-drawer__title">
          <i className="material-icons">school</i>
          Pedagogy review
          <span className="review-drawer__sub">section: {String(sectionId)}</span>
        </div>
        <div className="review-drawer__head-actions">
          <button
            type="button"
            className="review-drawer__retry"
            onClick={onRetry}
            disabled={loading}
            data-testid="review-drawer__retry"
            title="Re-run review"
          >
            <i className="material-icons">refresh</i>
          </button>
          <button
            type="button"
            className="review-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="material-icons">close</i>
          </button>
        </div>
      </header>
      <div className="review-drawer__body">
        {loading && (
          <div className="review-drawer__state" data-testid="review-drawer__loading">
            <i className="material-icons">hourglass_top</i>
            Running pedagogy review…
          </div>
        )}
        {!loading && error && (
          <div className="review-drawer__state review-drawer__state--error">
            <i className="material-icons">report</i>
            {error}
          </div>
        )}
        {!loading && !error && list.length === 0 && (
          <div className="review-drawer__state review-drawer__state--empty">
            <i className="material-icons">check_circle_outline</i>
            No findings — looks good.
          </div>
        )}
        {!loading && !error && list.length > 0 && (
          <ul className="review-drawer__list" data-testid="review-drawer__list">
            {list.map((rev, idx) => {
              const meta = severityMeta(rev.severity);
              const clickable = Boolean(rev.targetItemId);
              return (
                <li
                  key={`${rev.targetItemId || "_"}-${idx}`}
                  className={`review-drawer__row ${meta.className}${
                    clickable ? " review-drawer__row--clickable" : ""
                  }`}
                  data-testid="review-drawer__row"
                  onClick={clickable ? () => focusItem(rev.targetItemId) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            focusItem(rev.targetItemId);
                          }
                        }
                      : undefined
                  }
                  tabIndex={clickable ? 0 : -1}
                  role={clickable ? "button" : "listitem"}
                >
                  <i className="material-icons review-drawer__row-icon">{meta.icon}</i>
                  <div className="review-drawer__row-body">
                    <div className="review-drawer__row-head">
                      <span className="review-drawer__row-category">{rev.category}</span>
                      <span className="review-drawer__row-severity">{rev.severity}</span>
                    </div>
                    <div className="review-drawer__row-message">{rev.message}</div>
                    {rev.targetItemId && (
                      <div className="review-drawer__row-target">→ {rev.targetItemId}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
